/**
 * Auth Redux Slice
 * Actions and reducer for authentication state
 */

import { createSlice } from '@ufc/core'
import type { PayloadAction } from '@ufc/core'
import type { 
  AuthState, 
  AuthUser, 
  LoginCredentials, 
  RegisterData, 
  TenantSummary,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  ChangePasswordOtpRequest,
  DeleteAccountRequest,
  UpdateProfileRequest,
} from '../domain/types'

// ============================================================================
// Initial State
// ============================================================================

const initialState: AuthState = {
  user: null,
  token: null,
  status: 'idle',
  error: null,
  isInitialized: false,
  isAttemptBlocked: false,
  loginAttemptsRemaining: null,
  registrationStatus: 'idle',
  registrationMessage: null,
  passwordResetStatus: 'idle',
  passwordResetMessage: null,
  changePasswordStatus: 'idle',
  changePasswordMessage: null,
  changePasswordOtpStatus: 'idle',
  changePasswordOtpMessage: null,
  loading: {
    login: false,
    register: false,
    logout: false,
    passwordReset: false,
    sessionCheck: false,
    profileUpdate: false,
    changePassword: false,
    changePasswordOtp: false,
  },
}

// ============================================================================
// Slice
// ============================================================================

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // ========================================
    // Initialize Auth
    // ========================================
    initializeAuth: (state) => {
      state.status = 'loading'
      state.loading.sessionCheck = true
      state.registrationStatus = 'idle'
      state.registrationMessage = null
      state.passwordResetStatus = 'idle'
      state.passwordResetMessage = null
      state.changePasswordStatus = 'idle'
      state.changePasswordMessage = null
      state.changePasswordOtpStatus = 'idle'
      state.changePasswordOtpMessage = null
    },
    initializeAuthSuccess: (state, action: PayloadAction<{ user: AuthUser; token: string }>) => {
      state.user = action.payload.user
      state.token = action.payload.token
      state.status = 'authenticated'
      state.isInitialized = true
      state.error = null
      state.loading.sessionCheck = false
      state.registrationStatus = 'idle'
      state.registrationMessage = null
      state.passwordResetStatus = 'idle'
      state.passwordResetMessage = null
      state.changePasswordStatus = 'idle'
      state.changePasswordMessage = null
      state.changePasswordOtpStatus = 'idle'
      state.changePasswordOtpMessage = null
    },
    initializeAuthFailure: (state) => {
      state.user = null
      state.token = null
      state.status = 'unauthenticated'
      state.isInitialized = true
      state.error = null
      state.loading.sessionCheck = false
      state.registrationStatus = 'idle'
      state.registrationMessage = null
      state.passwordResetStatus = 'idle'
      state.passwordResetMessage = null
      state.changePasswordStatus = 'idle'
      state.changePasswordMessage = null
      state.changePasswordOtpStatus = 'idle'
      state.changePasswordOtpMessage = null
    },

    // ========================================
    // Login
    // ========================================
    login: (state, _action: PayloadAction<LoginCredentials>) => {
      state.status = 'loading'
      state.loading.login = true
      state.error = null
      state.isAttemptBlocked = false
      state.loginAttemptsRemaining = null
      state.registrationStatus = 'idle'
      state.registrationMessage = null
      state.passwordResetStatus = 'idle'
      state.passwordResetMessage = null
      state.changePasswordStatus = 'idle'
      state.changePasswordMessage = null
      state.changePasswordOtpStatus = 'idle'
      state.changePasswordOtpMessage = null
    },
    loginSuccess: (state, action: PayloadAction<{ user: AuthUser; token: string }>) => {
      state.user = action.payload.user
      state.token = action.payload.token
      state.status = 'authenticated'
      state.isInitialized = true
      state.error = null
      state.loading.login = false
      state.registrationStatus = 'idle'
      state.registrationMessage = null
      state.passwordResetStatus = 'idle'
      state.passwordResetMessage = null
      state.changePasswordStatus = 'idle'
      state.changePasswordMessage = null
      state.changePasswordOtpStatus = 'idle'
      state.changePasswordOtpMessage = null
    },
    loginFailure: (
      state,
      action: PayloadAction<{ message: string; isAttemptBlocked?: boolean; attemptsRemaining?: number }>
    ) => {
      state.status = 'error'
      state.error = action.payload.message
      state.user = null
      state.token = null
      state.isInitialized = true
      state.loading.login = false
      state.isAttemptBlocked = action.payload.isAttemptBlocked ?? false
      state.loginAttemptsRemaining = action.payload.attemptsRemaining ?? null
    },

    // ========================================
    // Register
    // ========================================
    register: (state, _action: PayloadAction<RegisterData>) => {
      state.status = 'loading'
      state.loading.register = true
      state.error = null
      state.registrationStatus = 'submitting'
      state.registrationMessage = null
      state.passwordResetStatus = 'idle'
      state.passwordResetMessage = null
      state.changePasswordStatus = 'idle'
      state.changePasswordMessage = null
      state.changePasswordOtpStatus = 'idle'
      state.changePasswordOtpMessage = null
    },
    registerSuccess: (state, action: PayloadAction<{ user: AuthUser; tenant?: TenantSummary; status: string; message: string }>) => {
      state.user = null
      state.token = null
      state.status = 'unauthenticated'
      state.error = null
      state.loading.register = false
      state.registrationStatus = 'success'
      state.registrationMessage = action.payload.message
    },
    registerFailure: (state, action: PayloadAction<string>) => {
      state.status = 'error'
      state.error = action.payload
      state.loading.register = false
      state.registrationMessage = null
      state.registrationStatus = 'error'
    },

    // ========================================
    // Logout
    // ========================================
    logout: (state) => {
      // Immediately clear auth state so protected UI unmounts before any other domain resets happen.
      // This prevents transient render errors during logout (especially in mobile WebViews).
      state.user = null
      state.token = null
      state.status = 'unauthenticated'
      state.error = null
      state.isInitialized = true
      state.loading.logout = true
      state.loading.login = false
      state.loading.register = false
      state.loading.passwordReset = false
      state.loading.sessionCheck = false
      state.loading.profileUpdate = false
      state.loading.changePassword = false
      state.loading.changePasswordOtp = false
      state.registrationStatus = 'idle'
      state.registrationMessage = null
      state.passwordResetStatus = 'idle'
      state.passwordResetMessage = null
      state.changePasswordStatus = 'idle'
      state.changePasswordMessage = null
      state.changePasswordOtpStatus = 'idle'
      state.changePasswordOtpMessage = null
    },
    logoutComplete: (state) => {
      state.user = null
      state.token = null
      state.status = 'unauthenticated'
      state.error = null
      state.loading.logout = false
      state.loading.login = false
      state.loading.register = false
      state.loading.passwordReset = false
      state.loading.sessionCheck = false
      state.loading.profileUpdate = false
      state.loading.changePassword = false
      state.loading.changePasswordOtp = false
      state.registrationStatus = 'idle'
      state.registrationMessage = null
      state.passwordResetStatus = 'idle'
      state.passwordResetMessage = null
      state.changePasswordStatus = 'idle'
      state.changePasswordMessage = null
      state.changePasswordOtpStatus = 'idle'
      state.changePasswordOtpMessage = null
    },

    // ========================================
    // Password Reset
    // ========================================
    forgotPassword: (state, _action: PayloadAction<ForgotPasswordRequest>) => {
      state.loading.passwordReset = true
      state.passwordResetStatus = 'requesting'
      state.passwordResetMessage = null
      state.error = null
    },
    forgotPasswordSuccess: (state, action: PayloadAction<{ message: string }>) => {
      state.loading.passwordReset = false
      state.passwordResetStatus = 'success'
      state.passwordResetMessage = action.payload.message
    },
    forgotPasswordFailure: (state, action: PayloadAction<string>) => {
      state.loading.passwordReset = false
      state.passwordResetStatus = 'error'
      state.passwordResetMessage = action.payload
    },

    resetPassword: (state, _action: PayloadAction<ResetPasswordRequest>) => {
      state.loading.passwordReset = true
      state.passwordResetStatus = 'requesting'
      state.passwordResetMessage = null
      state.error = null
    },
    resetPasswordSuccess: (state, action: PayloadAction<{ user?: AuthUser; token?: string; message: string }>) => {
      state.loading.passwordReset = false
      state.passwordResetStatus = 'success'
      state.passwordResetMessage = action.payload.message
      // Auto-login if token provided
      if (action.payload.user && action.payload.token) {
        state.user = action.payload.user
        state.token = action.payload.token
        state.status = 'authenticated'
        state.isInitialized = true
      }
    },
    resetPasswordFailure: (state, action: PayloadAction<string>) => {
      state.loading.passwordReset = false
      state.passwordResetStatus = 'error'
      state.passwordResetMessage = action.payload
    },

    // ========================================
    // Change Password
    // ========================================
    changePassword: (state, _action: PayloadAction<ChangePasswordRequest>) => {
      state.loading.changePassword = true
      state.changePasswordStatus = 'submitting'
      state.changePasswordMessage = null
      state.changePasswordOtpStatus = 'idle'
      state.changePasswordOtpMessage = null
      state.error = null
    },
    changePasswordSuccess: (state, action: PayloadAction<{ jwt: string; user: AuthUser; message: string }>) => {
      state.loading.changePassword = false
      state.changePasswordStatus = 'success'
      state.changePasswordMessage = action.payload.message
      state.token = action.payload.jwt
      state.user = action.payload.user
      state.status = 'authenticated'
      state.isInitialized = true
      state.error = null
    },
    changePasswordFailure: (state, action: PayloadAction<string>) => {
      state.loading.changePassword = false
      state.changePasswordStatus = 'error'
      state.changePasswordMessage = action.payload
    },
    requestChangePasswordOtp: (state, _action: PayloadAction<ChangePasswordOtpRequest>) => {
      state.loading.changePasswordOtp = true
      state.changePasswordOtpStatus = 'sending'
      state.changePasswordOtpMessage = null
      state.error = null
    },
    requestChangePasswordOtpSuccess: (
      state,
      action: PayloadAction<{ channel: 'email' | 'phone'; message: string }>
    ) => {
      state.loading.changePasswordOtp = false
      state.changePasswordOtpStatus = 'success'
      state.changePasswordOtpMessage = action.payload.message
    },
    requestChangePasswordOtpFailure: (state, action: PayloadAction<string>) => {
      state.loading.changePasswordOtp = false
      state.changePasswordOtpStatus = 'error'
      state.changePasswordOtpMessage = action.payload
    },

    // ========================================
    // Delete Account
    // ========================================
    deleteAccount: (state, _action: PayloadAction<DeleteAccountRequest>) => {
      state.loading.logout = true
      state.error = null
    },
    deleteAccountSuccess: (state) => {
      state.user = null
      state.token = null
      state.status = 'unauthenticated'
      state.error = null
      state.loading.logout = false
      state.loading.login = false
      state.loading.register = false
      state.loading.passwordReset = false
      state.loading.sessionCheck = false
      state.loading.profileUpdate = false
      state.loading.changePassword = false
      state.loading.changePasswordOtp = false
      state.registrationStatus = 'idle'
      state.registrationMessage = null
      state.passwordResetStatus = 'idle'
      state.passwordResetMessage = null
      state.changePasswordStatus = 'idle'
      state.changePasswordMessage = null
      state.changePasswordOtpStatus = 'idle'
      state.changePasswordOtpMessage = null
      state.isInitialized = true
    },
    deleteAccountFailure: (state, action: PayloadAction<string>) => {
      state.loading.logout = false
      state.error = action.payload
    },

    // ========================================
    // Profile Update
    // ========================================
    updateProfile: (state, _action: PayloadAction<UpdateProfileRequest>) => {
      state.loading.profileUpdate = true
      state.error = null
    },
    updateProfileSuccess: (state, action: PayloadAction<AuthUser>) => {
      state.loading.profileUpdate = false
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload,
        }
      } else {
        state.user = action.payload
      }
    },
    updateProfileFailure: (state, action: PayloadAction<string>) => {
      state.loading.profileUpdate = false
      state.error = action.payload
    },

    // ========================================
    // Clear Error
    // ========================================
    clearError: (state) => {
      state.error = null
    },

    // ========================================
    // Clear Lockout State
    // ========================================
    clearLockoutState: (state) => {
      state.isAttemptBlocked = false
      state.loginAttemptsRemaining = null
      state.error = null
    },

    // ========================================
    // Reset Messages
    // ========================================
    resetRegistration: (state) => {
      state.registrationStatus = 'idle'
      state.registrationMessage = null
    },

    resetPasswordReset: (state) => {
      state.passwordResetStatus = 'idle'
      state.passwordResetMessage = null
    },

    resetChangePasswordState: (state) => {
      state.changePasswordStatus = 'idle'
      state.changePasswordMessage = null
      state.changePasswordOtpStatus = 'idle'
      state.changePasswordOtpMessage = null
    },

    // ========================================
    // Update User
    // ========================================
    updateUser: (state, action: PayloadAction<Partial<AuthUser>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      }
    },
  },
})

// ============================================================================
// Exports
// ============================================================================

export const authActions = authSlice.actions
export const authReducer = authSlice.reducer
